import React, { useState, useEffect } from 'react';
import { tutorApi } from '../services/api';
import '../assets/TutorTutees.css';

const TutorTutees = ({ user }) => {
  const [tutees, setTutees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTutee, setSelectedTutee] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleData, setScheduleData] = useState({});

  useEffect(() => {
    fetchTutees();
  }, []);

  const fetchTutees = async () => {
    try {
      setLoading(true);
      const response = await tutorApi.getTutees();
      setTutees(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching tutees:', err);
      setError(`Failed to fetch tutees: ${err.response?.data?.message || err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = ['#893C3B', '#CBA763', '#839C3B', '#5B7C99', '#9B6B9E'];
    const index = name ? name.charCodeAt(0) % colors.length : 0;
    return colors[index];
  };

  const getTotalSubjects = () => {
    const uniqueSubjects = new Set();
    tutees.forEach(tutee => {
      if (tutee.subjects && tutee.subjects.length > 0) {
        tutee.subjects.forEach(subject => {
          uniqueSubjects.add(subject.name);
        });
      }
    });
    return uniqueSubjects.size;
  };

  const handleCardClick = (tutee) => {
    setSelectedTutee(tutee);
  };

  const closeModal = () => {
    setSelectedTutee(null);
    setEditingSchedule(null);
    setScheduleData({});
  };

  const handleEditSchedule = (subjectIndex, assignmentIndex) => {
    const key = `${subjectIndex}-${assignmentIndex}`;
    const subject = selectedTutee.subjects[subjectIndex];
    const assignment = subject.assignedTutors[assignmentIndex];
    
    setEditingSchedule(key);
    setScheduleData({
      [key]: assignment.schedules || []
    });
  };

  const handleCancelScheduleEdit = () => {
    setEditingSchedule(null);
    setScheduleData({});
  };

  const handleScheduleChange = (subjectIndex, assignmentIndex, scheduleIndex, field, value) => {
    const key = `${subjectIndex}-${assignmentIndex}`;
    const currentSchedules = [...(scheduleData[key] || [])];
    currentSchedules[scheduleIndex] = {
      ...currentSchedules[scheduleIndex],
      [field]: value
    };
    setScheduleData({
      ...scheduleData,
      [key]: currentSchedules
    });
  };

  const handleAddSchedule = (subjectIndex, assignmentIndex) => {
    const key = `${subjectIndex}-${assignmentIndex}`;
    const currentSchedules = scheduleData[key] || [];
    setScheduleData({
      ...scheduleData,
      [key]: [...currentSchedules, { day: '', startTime: '', frequency: 'weekly' }]
    });
  };

  const handleRemoveSchedule = (subjectIndex, assignmentIndex, scheduleIndex) => {
    const key = `${subjectIndex}-${assignmentIndex}`;
    const currentSchedules = [...(scheduleData[key] || [])];
    currentSchedules.splice(scheduleIndex, 1);
    setScheduleData({
      ...scheduleData,
      [key]: currentSchedules
    });
  };

  const handleSaveSchedule = async (subjectIndex, assignmentIndex) => {
    const key = `${subjectIndex}-${assignmentIndex}`;
    const schedules = scheduleData[key] || [];
    
    // Filter out incomplete schedules
    const validSchedules = schedules.filter(s => s.day && s.startTime);
    
    try {
      const subject = selectedTutee.subjects[subjectIndex];
      
      // Use tutor API to update schedule
      await tutorApi.updateTuteeSchedule(
        selectedTutee._id,
        subject._id,
        validSchedules
      );

      // Update local state
      const updatedSubjects = [...selectedTutee.subjects];
      updatedSubjects[subjectIndex] = {
        ...subject,
        assignedTutors: subject.assignedTutors.map((assignment, idx) => {
          if (idx === assignmentIndex) {
            return {
              ...assignment,
              schedules: validSchedules
            };
          }
          return assignment;
        })
      };

      setSelectedTutee({
        ...selectedTutee,
        subjects: updatedSubjects
      });

      // Update tutees list
      setTutees(tutees.map(t => 
        t._id === selectedTutee._id ? { ...t, subjects: updatedSubjects } : t
      ));

      setEditingSchedule(null);
      setScheduleData({});
    } catch (err) {
      console.error('Error saving schedule:', err);
      alert('Failed to save schedule. Please try again.');
    }
  };

  if (loading) return <div className="loading">Loading tutees...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="tutor-tutees">
      <div className="tutees-header">
        <h2>My Tutees</h2>
        <div className="header-stats">
          <p className="tutees-count">{tutees.length} {tutees.length === 1 ? 'Tutee' : 'Tutees'}</p>
          <p className="subjects-count">{getTotalSubjects()} {getTotalSubjects() === 1 ? 'Subject' : 'Subjects'}</p>
        </div>
      </div>
      
      <div className="tutees-list">
        {tutees.length === 0 ? (
          <div className="no-tutees">
            <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
              <circle cx="60" cy="60" r="58" stroke="#e5e7eb" strokeWidth="3"/>
              <circle cx="60" cy="45" r="18" stroke="#9ca3af" strokeWidth="3"/>
              <path d="M30 95c0-16.5 13.5-30 30-30s30 13.5 30 30" stroke="#9ca3af" strokeWidth="3" strokeLinecap="round"/>
            </svg>
            <h3>No Tutees Assigned Yet</h3>
            <p>You don't have any tutees assigned to you at the moment.</p>
            <p className="help-text">Please contact your administrator if you believe this is an error.</p>
          </div>
        ) : (
          <div className="tutees-grid">
            {tutees.map((tutee) => (
              <div 
                key={tutee._id} 
                className="tutee-card"
                onClick={() => handleCardClick(tutee)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => e.key === 'Enter' && handleCardClick(tutee)}
              >
                <div className="tutee-card-header">
                  <div 
                    className="tutee-avatar"
                    style={{ backgroundColor: getAvatarColor(tutee.name) }}
                  >
                    {getInitials(tutee.name)}
                  </div>
                  <div className="tutee-basic-info">
                    <h3>{tutee.name}</h3>
                    <p className="tutee-school">{tutee.school || 'School not specified'}</p>
                  </div>
                </div>

                <div className="tutee-card-body">
                  <div className="tutee-meta-bar">
                    <div className="meta-item">
                      <span className="meta-label">Grade</span>
                      <span className="meta-value">Year {tutee.year} • {tutee.level}</span>
                    </div>
                  </div>

                  <div className="subjects-section-enhanced">
                    {(() => {
                      // Filter subjects where this tutor is assigned
                      const mySubjects = tutee.subjects?.filter(subject => 
                        subject.assignedTutors?.some(assignment => {
                          const tutorId = assignment.tutor?._id?.toString()
                            || assignment.tutor?.toString()
                            || assignment._id?.toString()
                            || assignment.toString();
                          return tutorId === user._id?.toString() || tutorId === user._id;
                        })
                      ) || [];
                      
                      if (mySubjects.length === 0) {
                        return <span className="no-subjects-text">No subjects assigned</span>;
                      }
                      
                      return (
                        <div className="subjects-table">
                          {mySubjects.map((subject, index) => {
                            const myAssignment = subject.assignedTutors?.find(assignment => {
                              const tutorId = assignment.tutor?._id?.toString()
                                || assignment.tutor?.toString()
                                || assignment._id?.toString()
                                || assignment.toString();
                              return tutorId === user._id?.toString() || tutorId === user._id;
                            });
                            const scheduleCount = myAssignment?.schedules?.filter(s => s.day && s.startTime).length || 0;
                            const isPhysical = subject.mode?.toLowerCase() === 'physical';
                            const address = tutee.parent?.address || tutee.address;
                            
                            return (
                              <div key={index} className="subject-row-compact">
                                <div className="subject-row-header">
                                  <span className="subject-name-compact">{subject.name}</span>
                                  <span className={`mode-badge-compact ${subject.mode?.toLowerCase()}`}>
                                    {subject.mode?.charAt(0).toUpperCase() + subject.mode?.slice(1).toLowerCase()}
                                  </span>
                                </div>
                                
                                <div className="subject-row-details">
                                  <div className="detail-item-compact">
                                    <span className="detail-label">Rate:</span>
                                    <span className="detail-value">${myAssignment?.tutorRate || 'Default'}/hr</span>
                                  </div>
                                  
                                  {subject.frequencyPerMonth > 0 && (
                                    <div className="detail-item-compact">
                                      <span className="detail-label">Freq:</span>
                                      <span className="detail-value">{subject.frequencyPerMonth}/mo</span>
                                    </div>
                                  )}
                                  
                                  {scheduleCount > 0 && (
                                    <div className="detail-item-compact">
                                      <span className="detail-label">Schedules:</span>
                                      <span className="detail-value">{scheduleCount}</span>
                                    </div>
                                  )}
                                </div>
                                
                                {isPhysical && address && (
                                  <div className="address-row-compact">
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                      <path d="M6 1C4.067 1 2.5 2.567 2.5 4.5c0 2.5 3.5 6.5 3.5 6.5s3.5-4 3.5-6.5C9.5 2.567 7.933 1 6 1z" stroke="currentColor" strokeWidth="1.2" fill="none"/>
                                      <circle cx="6" cy="4.5" r="1.5" stroke="currentColor" strokeWidth="1.2" fill="none"/>
                                    </svg>
                                    <span className="address-text">{address}</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                <div className="tutee-card-footer">
                  <span className="view-profile-text">Click to view education details</span>
                  <svg className="arrow-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedTutee && (
        <div className="tutee-modal-overlay" onClick={closeModal}>
          <div className="tutee-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-section">
                <div 
                  className="modal-avatar"
                  style={{ backgroundColor: getAvatarColor(selectedTutee.name) }}
                >
                  {getInitials(selectedTutee.name)}
                </div>
                <div>
                  <h2>{selectedTutee.name}</h2>
                  <p className="modal-subtitle">Education Profile</p>
                </div>
              </div>
              <button className="close-button" onClick={closeModal} aria-label="Close">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <div className="education-section">
                <h3>Academic Information</h3>
                <div className="education-grid">
                  <div className="education-item">
                    <label>School</label>
                    <p>{selectedTutee.school || 'Not specified'}</p>
                  </div>
                  <div className="education-item">
                    <label>Level</label>
                    <p>{selectedTutee.level}</p>
                  </div>
                  <div className="education-item">
                    <label>Year</label>
                    <p>Year {selectedTutee.year}</p>
                  </div>
                  {selectedTutee.age && (
                    <div className="education-item">
                      <label>Age</label>
                      <p>{selectedTutee.age} years old</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="subjects-detail-section">
                <h3>Enrolled Subjects</h3>
                {selectedTutee.subjects && selectedTutee.subjects.length > 0 ? (
                  <div className="subjects-detail-list">
                    {selectedTutee.subjects.map((subject, subjectIndex) => {
                      // Find the assignment for the current tutor
                      // Handles both populated ({ tutor: { _id, ... } }) and unpopulated (plain ObjectId) formats
                      const myAssignment = subject.assignedTutors?.find(
                        assignment => {
                          const tutorId = assignment.tutor?._id?.toString()
                            || assignment.tutor?.toString()
                            || assignment._id?.toString()
                            || assignment.toString();
                          return tutorId === user._id?.toString() || tutorId === user._id;
                        }
                      );
                      const myAssignmentIndex = subject.assignedTutors?.findIndex(
                        assignment => {
                          const tutorId = assignment.tutor?._id?.toString()
                            || assignment.tutor?.toString()
                            || assignment._id?.toString()
                            || assignment.toString();
                          return tutorId === user._id?.toString() || tutorId === user._id;
                        }
                      );

                      if (!myAssignment) return null; // Only show subjects where this tutor is assigned

                      const editKey = `${subjectIndex}-${myAssignmentIndex}`;
                      const isEditing = editingSchedule === editKey;
                      const currentSchedules = isEditing 
                        ? (scheduleData[editKey] || [])
                        : (myAssignment.schedules || []);

                      return (
                        <div key={subjectIndex} className="subject-detail-card-enhanced">
                          <div className="subject-detail-header-enhanced">
                            <div className="subject-title-row">
                              <span className="subject-detail-name-enhanced">{subject.name}</span>
                              <div className="subject-badges">
                                <span className={`subject-detail-mode ${subject.mode?.toLowerCase()}`}>
                                  {subject.mode}
                                </span>
                                {subject.isActive !== false && (
                                  <span className="subject-status-badge active">Active</span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="subject-detail-body">
                            {/* Subject Info */}
                            <div className="subject-info-grid">
                              <div className="subject-info-item">
                                <svg className="icon" width="14" height="14" viewBox="0 0 16 16" fill="none">
                                  <path d="M2 3h12v10H2V3zm1 1v8h10V4H3z" fill="currentColor"/>
                                </svg>
                                <span className="info-label-small">My Rate:</span>
                                <span className="info-value-small">${myAssignment.tutorRate || 'Default'}/hr</span>
                              </div>
                              {subject.frequencyPerMonth > 0 && (
                                <div className="subject-info-item">
                                  <svg className="icon" width="14" height="14" viewBox="0 0 16 16" fill="none">
                                    <rect x="1" y="2" width="12" height="11" rx="1" stroke="currentColor" strokeWidth="1.2"/>
                                    <path d="M1 5h12M4 0v4M10 0v4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                                  </svg>
                                  <span className="info-label-small">Frequency:</span>
                                  <span className="info-value-small">{subject.frequencyPerMonth}/month</span>
                                </div>
                              )}
                              {subject.sessionDuration > 0 && (
                                <div className="subject-info-item">
                                  <svg className="icon" width="14" height="14" viewBox="0 0 16 16" fill="none">
                                    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                    <path d="M8 4v4l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                  </svg>
                                  <span className="info-label-small">Duration:</span>
                                  <span className="info-value-small">{subject.sessionDuration} hrs</span>
                                </div>
                              )}
                            </div>

                            {/* Address for Physical Lessons */}
                            {(subject.mode?.toLowerCase() === 'physical' && (selectedTutee.parent?.address || selectedTutee.address)) && (
                              <div className="address-section-modal">
                                <div className="address-header">
                                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M7 1C4.791 1 3 2.791 3 5c0 3 4 8 4 8s4-5 4-8c0-2.209-1.791-4-4-4z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                    <circle cx="7" cy="5" r="1.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                  </svg>
                                  <span className="address-label">Lesson Address</span>
                                </div>
                                <div className="address-content">
                                  {selectedTutee.parent?.address || selectedTutee.address}
                                </div>
                              </div>
                            )}

                            {/* Schedule Section */}
                            <div className="schedule-section-tutor">
                              <div className="schedule-header-tutor">
                                <label className="schedule-label-tutor">My Schedule</label>
                                {!isEditing && (
                                  <button
                                    className="btn-edit-schedule"
                                    onClick={() => handleEditSchedule(subjectIndex, myAssignmentIndex)}
                                  >
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                      <path d="M10 1.5a1.5 1.5 0 0 1 2.1 2.1L4.5 11.1l-3 .4.4-3L10 1.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Edit
                                  </button>
                                )}
                              </div>

                              {isEditing ? (
                                <div className="schedule-edit-mode">
                                  <div className="schedules-list-edit">
                                    {currentSchedules.length > 0 ? (
                                      currentSchedules.map((schedule, schedIdx) => (
                                        <div key={schedIdx} className="schedule-edit-item">
                                          <select
                                            className="schedule-day-select"
                                            value={schedule.day || ''}
                                            onChange={(e) => handleScheduleChange(subjectIndex, myAssignmentIndex, schedIdx, 'day', e.target.value)}
                                          >
                                            <option value="">Select Day</option>
                                            <option value="Monday">Monday</option>
                                            <option value="Tuesday">Tuesday</option>
                                            <option value="Wednesday">Wednesday</option>
                                            <option value="Thursday">Thursday</option>
                                            <option value="Friday">Friday</option>
                                            <option value="Saturday">Saturday</option>
                                            <option value="Sunday">Sunday</option>
                                          </select>
                                          <input
                                            type="time"
                                            className="schedule-time-input"
                                            value={schedule.startTime || ''}
                                            onChange={(e) => handleScheduleChange(subjectIndex, myAssignmentIndex, schedIdx, 'startTime', e.target.value)}
                                          />
                                          <button
                                            className="btn-remove-schedule"
                                            onClick={() => handleRemoveSchedule(subjectIndex, myAssignmentIndex, schedIdx)}
                                            title="Remove schedule"
                                          >
                                            ×
                                          </button>
                                        </div>
                                      ))
                                    ) : (
                                      <span className="no-schedule-text">No schedules yet</span>
                                    )}
                                  </div>
                                  <button
                                    className="btn-add-schedule-tutor"
                                    onClick={() => handleAddSchedule(subjectIndex, myAssignmentIndex)}
                                  >
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                      <path d="M6 2v8M2 6h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                    </svg>
                                    Add Schedule
                                  </button>
                                  <div className="schedule-actions">
                                    <button
                                      className="btn-cancel-schedule"
                                      onClick={handleCancelScheduleEdit}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      className="btn-save-schedule"
                                      onClick={() => handleSaveSchedule(subjectIndex, myAssignmentIndex)}
                                    >
                                      Save Schedule
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="schedules-display-tutor">
                                  {currentSchedules.length > 0 ? (
                                    currentSchedules.map((schedule, schedIdx) => (
                                      schedule.day && schedule.startTime && (
                                        <div key={schedIdx} className="schedule-badge-tutor">
                                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                            <rect x="1" y="2" width="10" height="9" rx="1" stroke="currentColor" strokeWidth="1"/>
                                            <path d="M1 4h10M3.5 1v2M8.5 1v2" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
                                          </svg>
                                          <span className="schedule-day-display">{schedule.day.substring(0, 3)}</span>
                                          <span className="schedule-time-display">{schedule.startTime}</span>
                                        </div>
                                      )
                                    ))
                                  ) : (
                                    <span className="no-schedule-text">No schedule set</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }).filter(Boolean)}
                  </div>
                ) : (
                  <p className="no-subjects-message">No subjects enrolled</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TutorTutees;